import { useEffect, useRef } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import type { ParsedUrlQuery } from 'querystring';
import { parse as parseQuerystring } from 'querystring';

import { adaptPageURLToProductsQueryActiveState } from 'src/adapters/pageURLToStore/adaptPageURLToProductsQueryActiveState';
import { adaptPageURLToStore } from 'src/adapters/pageURLToStore/adaptPageURLToStore';
import { parseFilterPath } from 'src/adapters/pageURLToStore/parseFilterPath';
import { buildShopListingFullUrl } from 'src/adapters/pageURLToStore/shop/serializeShopListingPath';
import { parseShopPath } from 'src/adapters/pageURLToStore/shop/shopTaxonomyPath';
import {
  adaptStoreToFilterPath,
  adaptStoreToPageURL,
} from 'src/adapters/storeToPageURL/adaptStoreToPageURL';
import { serializeURLSearchParamsStably } from 'src/adapters/storeToPageURL/serializeURLSearchParamsStably';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { requestProducts } from 'src/redux/products/thunks/requestProducts';
import { type RequestProductsMetadata, TriggerName } from 'src/redux/products/types';
import { deriveShopTaxonomyLevelsFromFacets } from 'src/redux/productsQuery/helpers/shop/deriveShopTaxonomyLevelsFromFacets';
import {
  type SyncProductsQueryFromURLPayload,
  actions,
  applyURLActiveStateToProductsQuery,
} from 'src/redux/productsQuery/slice';
import type { Facet } from 'src/redux/productsQuery/types/Facet';
import type { AppThunkDispatch } from 'src/redux/store';
import { PageType } from 'src/types/navigation';
import { scrollToTopOfProductsList } from 'src/utils/scrollToTopOfProductsList';
import { consumePendingProductsQueryURLSyncIntent } from './productsQueryURLSyncIntent';

const stripSlugParam = (query: ParsedUrlQuery): ParsedUrlQuery => {
  const { slug: _s, ...rest } = query;
  return rest;
};

/**
 * URL ↔ store sync helper used by SSR pages too. Generic flow: parses the filter-path segment
 * + the query-string for sort/page/term and produces the `syncFromURL` payload.
 */
export const getProductsQueryStateFromURL = (
  asPath: string,
  query: ParsedUrlQuery,
  state: Pick<ReduxApplicationState, 'navigation'>,
): SyncProductsQueryFromURLPayload => {
  const processedQuery = adaptPageURLToStore(query);
  return adaptPageURLToProductsQueryActiveState(processedQuery, {
    filterPathValues: parseFilterPath(asPath),
    includeSearchTerm: state.navigation.pageType === PageType.Search,
  });
};

const parseShopUrl = (
  asPath: string,
  routerQuery: ParsedUrlQuery,
): SyncProductsQueryFromURLPayload | { error: string } => {
  const q = asPath.indexOf('?');
  const pathPart = q === -1 ? asPath : asPath.slice(0, q);
  const search = q === -1 ? '' : asPath.slice(q + 1);

  const parsed = parseShopPath(pathPart);
  if (!parsed.ok) {
    return { error: parsed.error };
  }
  const mergedQuery = {
    ...stripSlugParam(routerQuery),
    ...(search ? (parseQuerystring(search) as ParsedUrlQuery) : {}),
  };

  const processedQuery = adaptPageURLToStore(mergedQuery);
  return {
    ...adaptPageURLToProductsQueryActiveState(processedQuery, {
      filterPathValues: parseFilterPath(pathPart),
      includeSearchTerm: false,
    }),
    shopTaxonomyLevels: parsed.taxonomyLevels,
  };
};

const serializeProductsQueryAsCategoryUrl = (state: ReduxApplicationState): string => {
  const filterPath = adaptStoreToFilterPath(state);
  const queryStr = serializeURLSearchParamsStably(
    adaptStoreToPageURL(state, state.productsQuery.page),
  );
  return queryStr ? `${filterPath}?${queryStr}` : filterPath;
};

const normalizeAsPathForCompare = (asPath: string): string => {
  const qi = asPath.indexOf('?');
  const path = qi === -1 ? asPath : asPath.slice(0, qi);
  if (qi === -1) {
    return path;
  }
  const stable = serializeURLSearchParamsStably(new URLSearchParams(asPath.slice(qi + 1)));
  return stable ? `${path}?${stable}` : path;
};

export const productsQueryMatchesURL = (
  asPath: string,
  query: ParsedUrlQuery,
  state: ReduxApplicationState,
): boolean => {
  const nextActive = getProductsQueryStateFromURL(asPath, query, state);
  const current = serializeProductsQueryAsCategoryUrl(state);
  const next = serializeProductsQueryAsCategoryUrl({
    ...state,
    productsQuery: applyURLActiveStateToProductsQuery(state.productsQuery, nextActive),
  });
  return current === next;
};

const matchesShopUrl = (asPath: string, state: ReduxApplicationState): boolean =>
  normalizeAsPathForCompare(asPath) === normalizeAsPathForCompare(buildShopListingFullUrl(state));

const sortAndPageFromState = (state: ReduxApplicationState, productsQuery = state.productsQuery) =>
  adaptStoreToPageURL({ ...state, productsQuery }, productsQuery.page);

export const getRouteChangeTriggerName = (
  state: ReduxApplicationState,
  nextActive: SyncProductsQueryFromURLPayload,
  filterSignature: (pq: ReduxApplicationState['productsQuery']) => string = (pq) =>
    adaptStoreToFilterPath({ productsQuery: pq }),
): TriggerName => {
  const currentPq = state.productsQuery;
  const nextPq = applyURLActiveStateToProductsQuery(currentPq, nextActive);

  const current = sortAndPageFromState(state, currentPq);
  const next = sortAndPageFromState(state, nextPq);

  const sortChanged = current.get('sort') !== next.get('sort');
  const pageChanged = current.get('page') !== next.get('page');
  const filterChanged = filterSignature(currentPq) !== filterSignature(nextPq);

  if (pageChanged && !filterChanged && !sortChanged) {
    return TriggerName.PAGE_NUMBER_CHANGE;
  }
  if (sortChanged && !filterChanged) {
    return TriggerName.CHANGE_SORT;
  }
  return TriggerName.CHANGE_FACETS;
};

const shopFilterSignature = (productsQuery: ReduxApplicationState['productsQuery']): string => {
  const filterPath = adaptStoreToFilterPath({ productsQuery });
  const levels = deriveShopTaxonomyLevelsFromFacets(
    productsQuery.facets,
    productsQuery.taxonomyAttributes,
  );
  return `${filterPath}|${JSON.stringify(levels)}`;
};

export const getFacetChangeMetadata = (
  currentFacets: Facet[],
  nextActiveFacetValuesByKey: SyncProductsQueryFromURLPayload['activeFacetValuesByKey'],
): RequestProductsMetadata | undefined => {
  const changedOptions = currentFacets.flatMap((currentFacet) => {
    const nextActiveValues = new Set(nextActiveFacetValuesByKey[currentFacet.alias] ?? []);
    return currentFacet.options.flatMap((currentOption) => {
      const nextActive = nextActiveValues.has(currentOption.value);
      if (currentOption.active === nextActive) {
        return [];
      }
      return [
        {
          facetTitle: currentFacet.title,
          optionActive: nextActive,
          optionTitle: currentOption.title,
        },
      ];
    });
  });

  return changedOptions.length === 1 ? changedOptions[0] : undefined;
};

export const useSyncProductsQueryFromUrl = () => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const router = useRouter();
  const lastHandledAsPath = useRef<string | null>(null);
  const state = useSelector((s: ReduxApplicationState) => s);
  const { navigation, productsQuery } = state;
  const isShop = navigation.pageType === PageType.Shop;

  useEffect(() => {
    if (!router.isReady || lastHandledAsPath.current === router.asPath) {
      return;
    }

    const nextActive = isShop
      ? parseShopUrl(router.asPath, router.query)
      : getProductsQueryStateFromURL(router.asPath, router.query, state);

    if ('error' in nextActive) {
      return;
    }

    const matches = isShop
      ? matchesShopUrl(router.asPath, state)
      : productsQueryMatchesURL(router.asPath, router.query, state);

    if (matches) {
      lastHandledAsPath.current = router.asPath;
      return;
    }
    lastHandledAsPath.current = router.asPath;

    batch(() => {
      const intent = consumePendingProductsQueryURLSyncIntent(router.asPath);
      const triggerName =
        intent?.triggerName ??
        getRouteChangeTriggerName(
          state,
          nextActive,
          isShop
            ? shopFilterSignature
            : (pq: ReduxApplicationState['productsQuery']) =>
                adaptStoreToFilterPath({ productsQuery: pq }),
        );
      const metadata =
        intent?.metadata ??
        (triggerName === TriggerName.CHANGE_FACETS
          ? getFacetChangeMetadata(productsQuery.facets, nextActive.activeFacetValuesByKey)
          : undefined);

      dispatch(actions.syncFromURL(nextActive));
      dispatch(requestProducts({ metadata, pageLoading: nextActive.page, triggerName }));
    });

    scrollToTopOfProductsList();
  }, [dispatch, isShop, productsQuery, router.asPath, router.isReady, router.query, state]);
};
